import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetTasksListQuery, useSearchTasksQuery, useGetTaskByIdQuery,
  type TaskFull,
} from '@/redux/internal-services/workspaceApiExtensions';
import { Spinner } from '@/components/UI/Spinner';
import TaskCard from './TaskCard';
import TaskListFilters from './TaskListFilters';
import { useAppSelector } from '@/hooks';
import { WORKSPACE_QUERY_OPTIONS } from '@/components/chat/workspaceQueryOptions';
import { useWorkspaceFilterReport, type OnWorkspaceFilterChange } from '@/hooks/useWorkspaceFilterReport';

// ─── Date helpers (called at interaction/render time, not module load) ─────────
function todayStr() { return new Date().toISOString().split('T')[0]; }
function daysAgoStr(n: number) { return new Date(Date.now() - n * 86400000).toISOString().split('T')[0]; }
function startOfWeekStr() {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().split('T')[0];
}
function endOfWeekStr() {
  const d = new Date();
  d.setDate(d.getDate() + (6 - d.getDay()));
  return d.toISOString().split('T')[0];
}

// ─── Types ─────────────────────────────────────────────────────────────────────
interface FilterState {
  status: string;
  priority: string;
  taskType: string;
  dueDateFrom: string;
  dueDateTo: string;
  overdue: boolean;
  assigneeUserId: string;
  ownerUserId: string;
  updatedByUserId: string;
}

interface Preset {
  id: string;
  label: string;
  icon: React.ReactNode;
  build: (userId: string) => Partial<FilterState>;
  activeCheck: (f: FilterState, userId: string) => boolean;
}

// SVG icon components
const IconUser = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);
const IconClock = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconTarget = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1" fill="currentColor"/>
  </svg>
);
const IconFlame = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M12 2c0 6-5 8-5 13a5 5 0 0010 0c0-5-5-7-5-13z"/><path d="M12 12c0 3-2 4-2 6a2 2 0 004 0c0-2-2-3-2-6z"/>
  </svg>
);
const IconAlert = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const PRESETS: Preset[] = [
  {
    id: 'my_tasks',
    label: 'My Tasks',
    icon: <IconUser />,
    build: (userId) => ({ assigneeUserId: userId }),
    activeCheck: (f, userId) => !!userId && f.assigneeUserId === userId,
  },
  {
    id: 'created_by_me',
    label: 'Created by Me',
    icon: <IconUser />,
    build: (userId) => ({ ownerUserId: userId }),
    activeCheck: (f, userId) => !!userId && f.ownerUserId === userId,
  },
  {
    id: 'updated_by_me',
    label: 'Updated by Me',
    icon: <IconUser />,
    build: (userId) => ({ updatedByUserId: userId }),
    activeCheck: (f, userId) => !!userId && f.updatedByUserId === userId,
  },
  {
    id: 'overdue',
    label: 'Overdue',
    icon: <IconClock />,
    build: () => ({ overdue: true, status: 'open' }),
    activeCheck: (f) => f.overdue && !f.dueDateFrom && !f.dueDateTo && f.status === 'open',
  },
  {
    id: 'overdue_week',
    label: 'Overdue This Week',
    icon: <IconCalendar />,
    build: () => ({ overdue: true, status: 'open', dueDateFrom: startOfWeekStr(), dueDateTo: todayStr() }),
    activeCheck: (f) => f.overdue && f.status === 'open' && f.dueDateFrom === startOfWeekStr() && f.dueDateTo === todayStr(),
  },
  {
    id: 'due_today',
    label: 'Due Today',
    icon: <IconTarget />,
    build: () => ({ dueDateFrom: todayStr(), dueDateTo: todayStr() }),
    activeCheck: (f) => !f.overdue && f.dueDateFrom === todayStr() && f.dueDateTo === todayStr(),
  },
  {
    id: 'due_this_week',
    label: 'Due This Week',
    icon: <IconCalendar />,
    build: () => ({ dueDateFrom: todayStr(), dueDateTo: endOfWeekStr() }),
    activeCheck: (f) => !f.overdue && f.dueDateFrom === todayStr() && f.dueDateTo === endOfWeekStr(),
  },
  {
    id: 'high_priority',
    label: 'High Priority',
    icon: <IconFlame />,
    build: () => ({ priority: 'high' }),
    activeCheck: (f) => f.priority === 'high' && !f.overdue,
  },
  {
    id: 'critical_overdue',
    label: 'Critical Overdue 2d+',
    icon: <IconAlert />,
    build: () => ({ priority: 'urgent', overdue: true, dueDateTo: daysAgoStr(2) }),
    activeCheck: (f) => f.priority === 'urgent' && f.overdue && f.dueDateTo === daysAgoStr(2),
  },
];

// ─── Preset chip ───────────────────────────────────────────────────────────────
const PresetChip: React.FC<{ label: string; icon: React.ReactNode; active: boolean; onClick: () => void }> = ({ label, icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all whitespace-nowrap ${
      active
        ? 'bg-brand-dark border-brand-dark text-white'
        : 'border-[#EDEDED] bg-ui-subtle text-ui-text2 hover:border-brand-dark/25 hover:text-ui-text'
    }`}
  >
    <span className="flex-shrink-0">{icon}</span>
    {label}
    {active && (
      <span className="flex-shrink-0 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/20">
        <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </span>
    )}
  </button>
);

// ─── Component ─────────────────────────────────────────────────────────────────
interface TaskListProps {
  initialStatus?: string;
  initialPriority?: string;
  initialAssigneeUserId?: string;
  initialOwnerUserId?: string;
  initialUpdatedByUserId?: string;
  initialSearch?: string;
  initialOverdue?: boolean;
  /** When false (SAM workspace mode): shows all tenant tasks instead of only the current user's. Defaults to true. */
  initialMine?: boolean;
  /** When true (SAM workspace mode): hides all controls. */
  hideControls?: boolean;
  /** Called when a card is clicked (workspace mode). Defaults to navigate('/tasks/:id'). */
  onSelectTask?: (taskId: string) => void;
  /** Rows SAM's `get_tasks` tool already fetched for this exact filter (workspace mode). Rendered
   *  immediately in place of a loading spinner while this component's own first fetch is still in
   *  flight, so the card never flashes empty while the tool's narrated count is already on screen.
   *  Once the live query resolves it takes over as the source of truth (this is a seed, not a
   *  substitute) — any user interaction (filter/search/page) always goes through the live query. */
  initialRecords?: TaskFull[];
  /** Task ID to auto-expand inline on mount (workspace open_task flow). */
  initialExpandedId?: string;
  initialSortBy?: string;
  initialSortDir?: number;
  /** When set (e.g. opened directly from a notification link), show ONLY this task — no
   *  filters, chips, search, or pagination — instead of the full list. */
  singleTaskId?: string;
  /** Called when the user leaves single-task mode to go back to browsing the full list. */
  onExitSingleTask?: () => void;
  /** Workspace mode: report a MANUALLY changed filter so it joins the "⋯" recent-views menu
   *  alongside the agent-driven views. See `useWorkspaceFilterReport`. */
  onFiltersChange?: OnWorkspaceFilterChange;
}

const TaskList: React.FC<TaskListProps> = ({
  initialStatus = 'open', initialPriority = '', initialAssigneeUserId = '', initialOwnerUserId = '',
  initialUpdatedByUserId = '', initialSearch = '', initialOverdue = false, initialMine = true, hideControls = false, onSelectTask, initialRecords, initialExpandedId, initialSortBy, initialSortDir,
  singleTaskId, onExitSingleTask, onFiltersChange,
}) => {
  const { t } = useTranslation();
  const singleTaskResult = useGetTaskByIdQuery(singleTaskId as string, { ...WORKSPACE_QUERY_OPTIONS, skip: !singleTaskId });
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(initialExpandedId ?? null);

  useEffect(() => {
    if (initialExpandedId !== undefined) setExpandedTaskId(initialExpandedId ?? null);
  }, [initialExpandedId]);

  const [isQueueView, setIsQueueView] = useState(false);

  const [status, setStatus] = useState(initialStatus);
  const [priority, setPriority] = useState(initialPriority);
  const [taskType, setTaskType] = useState('');
  const [dueDateFrom, setDueDateFrom] = useState('');
  const [dueDateTo, setDueDateTo] = useState('');
  const [overdue, setOverdue] = useState(initialOverdue);
  const [assigneeUserId, setAssigneeUserId] = useState(initialAssigneeUserId);
  const [ownerUserId, setOwnerUserId] = useState(initialOwnerUserId);
  const [updatedByUserId, setUpdatedByUserId] = useState(initialUpdatedByUserId);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [sortBy] = useState(initialSortBy ?? '');
  const [sortDir] = useState(initialSortDir ?? -1);
  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const limit = 20;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput.trim()), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchInput]);

  useEffect(() => {
    if (searchExpanded) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [searchExpanded]);

  // status='open' is the default — don't count it as a user-applied filter. status='' means the
  // user explicitly picked "All" (there's no separate "unset" state) and DOES count — the old
  // `status && status !== 'open' ? status : ''` short-circuited on the falsy '' before the
  // !== 'open' check ever ran, so explicitly choosing "All" silently didn't count as a filter,
  // and "Clear filters" never appeared even though the result set differed from the default.
  const activeCount = [status !== 'open' ? (status || 'all') : '', priority, taskType, dueDateFrom, dueDateTo, overdue ? 'x' : '', assigneeUserId, ownerUserId, updatedByUserId, isQueueView ? 'x' : ''].filter(Boolean).length;
  const isSearching = searchQuery.length > 0;

  // When overdue=true with no explicit dueDateTo, cap due_date_to at end of today so the
  // backend returns only tasks whose due date is on or before today (i.e. overdue/due).
  const effectiveDueDateTo = overdue && !dueDateTo ? todayStr() : dueDateTo;

  // The list's effective filter. Extracted so the SAME object feeds both the query and the
  // recent-views report below — the "⋯" menu entry then replays exactly what is on screen.
  const queryArgs = !isSearching ? {
    ...(isQueueView
      ? { my_queues: true, skip: page * limit, limit }
      : {
          mine: ownerUserId || updatedByUserId ? false : initialMine,
          status: status || undefined,
          priority: priority || undefined,
          task_type: taskType || undefined,
          assignee_user_id: assigneeUserId || undefined,
          owner_user_id: ownerUserId || undefined,
          updated_by_user_id: updatedByUserId || undefined,
          due_date_from: dueDateFrom ? new Date(dueDateFrom).toISOString() : undefined,
          due_date_to: effectiveDueDateTo ? new Date(`${effectiveDueDateTo}T23:59:59`).toISOString() : undefined,
          sort_by: sortBy || undefined,
          sort_dir: sortBy ? sortDir : undefined,
          skip: page * limit,
          limit,
        }),
  } : undefined;

  const listResult = useGetTasksListQuery(queryArgs, { ...WORKSPACE_QUERY_OPTIONS, skip: isSearching });

  // Report user-driven filter changes (chips, search, queue toggle) to the "⋯" recent-views menu.
  useWorkspaceFilterReport(
    isSearching ? { search: searchQuery } : (queryArgs ?? {}),
    onFiltersChange,
  );

  const searchResult = useSearchTasksQuery(searchQuery, { skip: !isSearching });

  // Seed placeholder: only covers the very first, still-in-flight fetch — the moment listResult
  // has real data (including an empty page), that takes over as the source of truth.
  const hasSeed = initialRecords !== undefined;
  const usingSeed = !isSearching && hasSeed && listResult.isLoading && !listResult.data;

  const tasks: TaskFull[] = isSearching
    ? (searchResult.data ?? [])
    : usingSeed ? initialRecords! : (listResult.data?.data ?? []);
  const total = isSearching ? tasks.length : usingSeed ? initialRecords!.length : (listResult.data?.total ?? 0);
  const isLoading = isSearching ? searchResult.isLoading : (listResult.isLoading && !usingSeed);
  const isError = isSearching ? searchResult.isError : listResult.isError;
  const totalPages = isSearching ? 1 : Math.ceil(total / limit);

  const clearAll = () => {
    setStatus('open'); setPriority(''); setTaskType('');
    setDueDateFrom(''); setDueDateTo(''); setOverdue(false);
    setAssigneeUserId(''); setOwnerUserId(''); setUpdatedByUserId(''); setPage(0);
  };

  const currentFilters: FilterState = { status, priority, taskType, dueDateFrom, dueDateTo, overdue, assigneeUserId, ownerUserId, updatedByUserId };

  const applyPreset = (preset: Preset) => {
    setIsQueueView(false);
    const isActive = preset.activeCheck(currentFilters, currentUserId);
    clearAll();
    if (isActive) return; // toggle off
    const vals = preset.build(currentUserId);
    if (vals.status !== undefined) setStatus(vals.status);
    if (vals.priority !== undefined) setPriority(vals.priority);
    if (vals.taskType !== undefined) setTaskType(vals.taskType);
    if (vals.dueDateFrom !== undefined) setDueDateFrom(vals.dueDateFrom);
    if (vals.dueDateTo !== undefined) setDueDateTo(vals.dueDateTo);
    if (vals.overdue !== undefined) setOverdue(vals.overdue);
    if (vals.assigneeUserId !== undefined) setAssigneeUserId(vals.assigneeUserId);
    if (vals.ownerUserId !== undefined) setOwnerUserId(vals.ownerUserId);
    if (vals.updatedByUserId !== undefined) setUpdatedByUserId(vals.updatedByUserId);
    setPage(0);
  };

  const visiblePresets = PRESETS.filter((p) => p.id !== 'my_tasks' || !!currentUserId);


  // ── Role Queue chip (appended after PRESETS row) ────────────────────────────
  const IconUsers = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );

  if (singleTaskId) {
    return (
      <div className="flex flex-col min-h-full space-y-2">
        {onExitSingleTask && (
          <button
            type="button"
            onClick={onExitSingleTask}
            className="self-start text-[11px] font-medium text-brand-dark hover:underline"
          >
            {t('tasks.viewAll')}
          </button>
        )}
        {singleTaskResult.isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
        {singleTaskResult.isError && <p className="text-center text-[#CF4148] py-8">Failed to load task.</p>}
        {singleTaskResult.data && (
          <div className="grid grid-cols-1 gap-3 pb-2">
            <TaskCard
              task={singleTaskResult.data}
              onSelect={onSelectTask}
              expandedId={singleTaskId}
              onExpand={() => {}}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full space-y-2">

      {/* ── Controls ── */}
      {!hideControls && (
        <>
          {/* ── Top control row ── */}
          <div className="flex items-center gap-2">

            {/* Search — icon (collapsed) or full input (expanded) */}
            {searchExpanded ? (
              <div className="relative flex-1 min-w-0">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ui-text3 pointer-events-none"
                  width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search tasks…"
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); setPage(0); }}
                  className="w-full pl-8 pr-8 py-1.5 rounded-lg border border-[#EDEDED] bg-ui-subtle text-xs text-ui-text placeholder:text-ui-text3 focus:outline-none focus:border-brand-dark/40 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearchExpanded(false); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ui-text3 hover:text-ui-text transition-colors"
                  title="Close search"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ) : (
              <>
                {/* Search icon button */}
                <button
                  type="button"
                  onClick={() => setSearchExpanded(true)}
                  title="Search tasks"
                  className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg border transition-colors ${
                    searchInput
                      ? 'bg-brand-dark/10 border-brand-dark/30 text-brand-dark'
                      : 'border-[#EDEDED] bg-ui-subtle text-ui-text2 hover:border-brand-dark/25 hover:text-ui-text'
                  }`}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </button>

                {/* Preset chips fill remaining space */}
                <div className="flex-1 overflow-x-auto no-scrollbar min-w-0">
                  <div className="flex gap-1.5 min-w-max">
                    {visiblePresets.map((preset) => (
                      <PresetChip
                        key={preset.id}
                        label={preset.label}
                        icon={preset.icon}
                        active={preset.activeCheck(currentFilters, currentUserId)}
                        onClick={() => applyPreset(preset)}
                      />
                    ))}
                    {/* Role Queue chip */}
                    <PresetChip
                      label="Role Queue"
                      icon={<IconUsers />}
                      active={isQueueView}
                      onClick={() => { setIsQueueView((v) => !v); if (isQueueView) clearAll(); }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Status indicator chip — always visible so the current status filter is unambiguous */}
            {!isQueueView && !isSearching && (
              <button
                type="button"
                onClick={() => setShowFilters(true)}
                title="Click to change status filter"
                className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  status === 'completed'
                    ? 'border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : status === 'cancelled'
                    ? 'border-gray-300 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    : status === ''
                    ? 'border-[#EDEDED] bg-ui-subtle text-ui-text3 hover:border-brand-dark/25'
                    : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  status === 'completed' ? 'bg-green-500' : status === 'cancelled' ? 'bg-gray-400' : status === '' ? 'bg-ui-text3' : 'bg-blue-500'
                }`} />
                {status === '' ? 'All' : status === 'open' ? 'Open' : status === 'completed' ? 'Done' : status === 'cancelled' ? 'Cancelled' : status}
              </button>
            )}

            {/* Filter button — always visible */}
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                showFilters || activeCount > 0
                  ? 'bg-brand-dark/10 border-brand-dark/30 text-brand-dark'
                  : 'border-[#EDEDED] bg-ui-subtle text-ui-text2 hover:border-brand-dark/25 hover:text-ui-text'
              }`}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              Filter
              {activeCount > 0 && (
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-brand-dark text-white text-[9px] font-bold leading-none">
                  {activeCount}
                </span>
              )}
            </button>

          </div>

          {/* Clear filters link */}
          {activeCount > 0 && !showFilters && (
            <div className="flex items-center justify-end">
              <button type="button" onClick={clearAll} className="text-[11px] text-ui-text3 hover:text-brand-dark transition-colors">
                Clear filters
              </button>
            </div>
          )}

          {isSearching && (
            <p className="text-[11px] text-ui-text3">
              Searching for <span className="font-medium text-ui-text">"{searchQuery}"</span>
              {' — '}filters are paused while searching.
            </p>
          )}
        </>
      )}

      {/* ── Chip row (hideControls / workspace mode) ── */}
      {hideControls && (
        <div className="overflow-x-auto no-scrollbar -mx-0.5 px-0.5">
          <div className="flex gap-1.5 min-w-max">
            {visiblePresets.map((preset) => (
              <PresetChip
                key={preset.id}
                label={preset.label}
                icon={preset.icon}
                active={preset.activeCheck(currentFilters, currentUserId)}
                onClick={() => { setIsQueueView(false); applyPreset(preset); }}
              />
            ))}
            <PresetChip
              label="Role Queue"
              icon={<IconUsers />}
              active={isQueueView}
              onClick={() => { setIsQueueView((v) => !v); if (isQueueView) clearAll(); }}
            />
          </div>
        </div>
      )}

      {/* ── Filter panel — inline below controls, above task list ── */}
      {showFilters && !hideControls && (
        <div className="rounded-xl border border-[#EDEDED] bg-ui-card shadow-sm px-3 pt-2.5 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold text-ui-text2 uppercase tracking-wide">Filter</span>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <button type="button" onClick={clearAll} className="text-[11px] text-ui-text3 hover:text-brand-dark transition-colors">
                  Clear all
                </button>
              )}
              <button type="button" onClick={() => setShowFilters(false)} className="text-ui-text3 hover:text-ui-text transition-colors p-0.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
          <TaskListFilters
            status={status}
            priority={priority}
            taskType={taskType}
            dueDateFrom={dueDateFrom}
            dueDateTo={dueDateTo}
            overdue={overdue}
            onStatusChange={(v) => { setStatus(v); setPage(0); }}
            onPriorityChange={(v) => { setPriority(v); setPage(0); }}
            onTaskTypeChange={(v) => { setTaskType(v); setPage(0); }}
            onDueDateFromChange={(v) => { setDueDateFrom(v); setPage(0); }}
            onDueDateToChange={(v) => { setDueDateTo(v); setPage(0); }}
            onOverdueChange={(v) => { setOverdue(v); setPage(0); }}
          />
        </div>
      )}

      {/* ── Task list (shared for normal, search, and role-queue views) ── */}
      <>
        {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
        {isError && <p className="text-center text-[#CF4148] py-8">Failed to load tasks.</p>}
        {!isLoading && !isError && tasks.length === 0 && (
          <p className="text-center text-ui-text3 py-8">
            {isQueueView ? 'No tasks in your role queue.' : 'No tasks found.'}
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 pb-2">
          {tasks.map((task) => (
            <TaskCard
              key={task.task_id}
              task={task}
              onSelect={onSelectTask}
              expandedId={expandedTaskId}
              onExpand={setExpandedTaskId}
            />
          ))}
        </div>

        {!hideControls && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-[#EDEDED] disabled:opacity-40 text-ui-text hover:bg-ui-hover"
            >
              Previous
            </button>
            <span className="text-sm text-ui-text2">{page + 1} / {totalPages}</span>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-[#EDEDED] disabled:opacity-40 text-ui-text hover:bg-ui-hover"
            >
              Next
            </button>
          </div>
        )}
      </>

    </div>
  );
};

export default TaskList;
